/**
 * ClientContextBanner — top-of-page banner the AI pins when it's
 * actively working on something ("Whipping HR 28...", "Checking
 * Smith's vote history...", etc.).
 *
 * Subscribes to the window `cmcp:client-context` CustomEvent, fired
 * by useBrowserBus when a `set_client_context` choreography event
 * arrives over SSE. Empty payload (or a `clear_visual_state` event)
 * dismisses the banner.
 *
 * Intentionally minimal — host apps mount it in their Layout above
 * the page content; styling can be overridden via the className prop.
 */

import { useEffect, useState } from "react";
import { AlertTriangle, Info, Loader2, X, type LucideIcon } from "lucide-react";

export interface ClientContextBannerProps {
  /** Optional className override for the banner wrapper. */
  className?: string;
}

interface BannerState {
  text: string;
  kind: "info" | "working" | "warning";
}

const KIND_STYLES: Record<BannerState["kind"], string> = {
  info: "bg-info-soft border-info/20 text-info",
  working: "bg-accent/40 border-accent text-ink",
  warning: "bg-danger-soft border-danger/20 text-danger",
};

const KIND_ICON: Record<BannerState["kind"], LucideIcon> = {
  info: Info,
  working: Loader2,
  warning: AlertTriangle,
};

export function ClientContextBanner({ className }: ClientContextBannerProps) {
  const [state, setState] = useState<BannerState | null>(null);

  useEffect(() => {
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) {
        setState(null);
        return;
      }
      const kind = (detail.kind ?? "info") as BannerState["kind"];
      const text = String(detail.text ?? "");
      if (!text) {
        setState(null);
        return;
      }
      setState({ text, kind: KIND_STYLES[kind] ? kind : "info" });
    };
    window.addEventListener("cmcp:client-context", onEvent);
    return () => window.removeEventListener("cmcp:client-context", onEvent);
  }, []);

  if (!state) return null;
  const KindIcon = KIND_ICON[state.kind];

  return (
    <div
      className={
        "px-4 py-2 border-b text-xs font-medium flex items-center gap-2 " +
        KIND_STYLES[state.kind] +
        (className ? " " + className : "")
      }
      role="status"
      aria-live="polite"
    >
      <KindIcon size={14} className={`shrink-0${state.kind === "working" ? " animate-spin" : ""}`} aria-hidden />
      <span className="flex-1 truncate">{state.text}</span>
      <button
        onClick={() => setState(null)}
        className="text-current opacity-50 hover:opacity-100 p-0.5 rounded"
        aria-label="Dismiss"
        title="Dismiss"
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
}
